/* -----------------Globals--------------- */
import { useMemo, useState } from "react";

/* -----------------UI--------------- */
import {
	ImageBackground,
	type ImageContentFit,
	type ImageStyle,
} from "expo-image";
import { ActivityIndicator, TouchableOpacity } from "react-native";

import { isWeb } from "@memorang/helpers/platform";
import { useImageDimensions } from "@react-native-community/hooks";
/* -----------------Helpers & Hooks--------------- */
import { useAppTheme } from "../../useAppTheme";

/* -----------------Child components--------------- */
import { Text } from "react-native-paper";
import ImageViewerModal from "../ImageViewerModal";

type BaseProps = {
	url: string;
	name?: string;
	allImages: { url: string; caption?: string }[];
	borderRadius?: number;
	initialIndex?: number;
	contentFit?: ImageContentFit;
};

type FixedSizeProps = (ImageStyle | undefined) & {
	width?: number;
	height?: number;
};

type AutoSizeProps = {
	/** Enable intrinsic-size driven layout (used for single image) */
	autoSize?: boolean;
	/** Container width to fill (acts like maxWidth: 100%) */
	containerWidth?: number;
	/** Clamp height for tall images (e.g., 200–360) */
	maxHeight?: number;
};

type Props = BaseProps & FixedSizeProps & AutoSizeProps;

function useImageRenderSize(params: {
	url: string;
	autoSize?: boolean;
	containerWidth?: number;
	maxHeight?: number;
	width?: number;
	height?: number;
}) {
	const {
		url,
		autoSize,
		containerWidth,
		maxHeight = 320,
		width,
		height,
	} = params;

	const { dimensions, loading } = useImageDimensions({
		uri: url,
	});

	const isFixed =
		!autoSize && typeof width === "number" && typeof height === "number";

	const { displayWidth, displayHeight } = useMemo(() => {
		if (isFixed) {
			return { displayWidth: width as number, displayHeight: height as number };
		}

		const aspect =
			dimensions?.width && dimensions?.height
				? dimensions.width / dimensions.height
				: 1;

		const w = autoSize && containerWidth ? containerWidth : (width ?? 200);
		const h = Math.min(maxHeight, w / (aspect || 1));

		return { displayWidth: w, displayHeight: h };
	}, [
		isFixed,
		width,
		height,
		autoSize,
		containerWidth,
		maxHeight,
		dimensions?.width,
		dimensions?.height,
	]);

	const showLoader = Boolean(autoSize && loading);

	return { displayWidth, displayHeight, showLoader };
}

export const ZoomImage = ({
	url,
	borderRadius,
	name,
	allImages,
	initialIndex,
	contentFit = "cover",

	width,
	height,
	autoSize,
	containerWidth,
	maxHeight,

	...imageStyle
}: Props) => {
	const [showFullScreenImage, setShowFullScreenImage] = useState(false);
	const theme = useAppTheme();

	const { displayWidth, displayHeight, showLoader } = useImageRenderSize({
		url,
		autoSize,
		containerWidth,
		maxHeight,
		width,
		height,
	});

	const toggleFullScreenImage = () => setShowFullScreenImage((p) => !p);

	return (
		<TouchableOpacity
			onLongPress={toggleFullScreenImage}
			onPress={toggleFullScreenImage}
		>
			{url ? (
				showLoader ? (
					<ActivityIndicator size="small" />
				) : (
					<ImageBackground
						style={
							{
								width: displayWidth,
								height: displayHeight,
								marginTop: 8,
								position: "relative",
								justifyContent: "flex-end",
								borderRadius: borderRadius || theme.roundness,
								borderWidth: 1,
								borderColor: theme.colors.outlineVariant,
								alignItems: "center",
								alignSelf: "center",
								...(imageStyle || {}),
								...(isWeb ? { cursor: "zoom-in" } : {}),
							} as ImageStyle
						}
						imageStyle={{
							borderRadius: borderRadius || theme.roundness,
						}}
						contentFit={contentFit}
						contentPosition="center"
						cachePolicy="memory-disk"
						source={{ uri: url }}
					>
						{name && (
							<Text
								variant="labelMedium"
								style={{
									color: "#fff",
									fontFamily: "OpenSans-SemiBold",
									paddingBottom: 8,
									textAlign: "center",
								}}
							>
								{name}
							</Text>
						)}
					</ImageBackground>
				)
			) : null}

			<ImageViewerModal
				visible={showFullScreenImage}
				images={allImages}
				initialIndex={initialIndex}
				handleClose={() => setShowFullScreenImage(false)}
			/>
		</TouchableOpacity>
	);
};
